import { Collection, Database, Q } from '@nozbe/watermelondb';

import {
    OrganisationModel,
    OrganisationPayload,
    PreparedDataOnOrganisationUpdate,
} from '../../types/Organisation';
import {
    InvoiceDBServiceOptions,
    OrganisationDBServiceOptions,
} from '../../types/dbService';
import { UserModel, UserPayload } from '../../types/User';
import {
    appendPreparedInvoices,
    appendPreparedOrganisation,
    appendPreparedUser,
    addRelatedDataBatch,
    collectOrganisationInvoiceUpdateData,
    logUpdateDBActions,
    prepareOrganisationInvoiceUpdates,
    prepareUpdateOrganisation,
    prepareUpdateUserFromPostPayload,
    shouldUpdateInvoicesTaxRates,
} from './helpers/organisation/organisationUpdate';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';

class Organisation {
    private database: Database;
    private collection: Collection<OrganisationModel>;
    private table = 'organisations';
    private options: OrganisationDBServiceOptions;
    private validation: OrganisationDBServiceOptions['validation'];

    constructor(options: OrganisationDBServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
        this.validation = options.validation;
    }

    get() {
        return this.collection.query().fetch();
    }

    getCurrentOrganisationQuery() {
        return this.collection.query();
    }

    getById(id: string) {
        return this.collection.query(Q.where('id', id)).fetch();
    }

    async update({
        payload,
        userId,
        postPayload,
    }: {
        payload: OrganisationPayload;
        userId: string;
        postPayload?: { userPayload?: UserPayload };
    }) {
        const invoiceServiceOptions: InvoiceDBServiceOptions = {
            database: this.database,
            imageService: this.options.imageService,
            logger: {
                logDBAction: this.options.logDBAction,
                error: () => null,
            },
            validation: this.validation,
        };

        const { image, avatar, removeAvatar, taxRate, productsTaxRate } =
            payload;

        const organiastionElement = await this.get();
        const { id: organisationID } = organiastionElement[0];
        const orgElement = organiastionElement[0];

        const updatedOrganisation = await this.database.write(async () => {
            const userToUpdate = postPayload?.userPayload
                ? await this.database.collections
                      .get<UserModel>('users')
                      .find(userId)
                : undefined;

            const invoiceUpdateData = shouldUpdateInvoicesTaxRates({ payload })
                ? await collectOrganisationInvoiceUpdateData({
                      database: this.database,
                      invoiceServiceOptions,
                      payload,
                      taxRate,
                      productsTaxRate,
                  })
                : [];

            const preparedOrganisation = prepareUpdateOrganisation({
                orgElement,
                payload,
            });
            const preparedInvoiceUpdates =
                prepareOrganisationInvoiceUpdates(invoiceUpdateData);
            const preparedUser = userToUpdate
                ? prepareUpdateUserFromPostPayload({
                      user: userToUpdate,
                      userPayload: postPayload!.userPayload!,
                  })
                : undefined;

            let preparedData: PreparedDataOnOrganisationUpdate[] =
                appendPreparedOrganisation([], preparedOrganisation);
            preparedData = appendPreparedInvoices(
                preparedData,
                preparedInvoiceUpdates,
            );

            if (preparedUser) {
                preparedData = appendPreparedUser(preparedData, preparedUser);
            }

            validateEntityRecords(
                preparedData.filter((record) => record.record != null),
                this.validation,
            );

            await addRelatedDataBatch(
                preparedData.map((item) => item.record),
                this.database,
            );

            logUpdateDBActions({
                orgElement,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
                userUpdated: preparedUser,
            });

            return preparedOrganisation;
        }, 'update-organisation');

        if (removeAvatar) {
            await this.options.imageService.remove(organisationID);
        }

        if (image && image.uri) {
            this.options.imageService.uploadImage({
                image,
                entityID: organisationID,
                entityType: 'Organisation',
                annotationImage: '',
                ownerID: userId,
                userIDs: [userId],
                documentID: avatar?.documentID,
                organisationID,
            });
        }

        return updatedOrganisation;
    }
}

export default Organisation;
